import { useNavigate, useParams } from 'react-router-dom';
import { LogOut, Settings, Users, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';

export function UserProfileDropdown() {
  const { user, signOut, isLoading: authLoading } = useAuth();
  const { currentOrganization, membership, isLoading: orgLoading } = useOrganization();
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  // Show skeleton while data loads (shell renders immediately now)
  if (authLoading || orgLoading) {
    return (
      <div className="flex w-full items-center gap-2 rounded-md p-2">
        <Skeleton className="h-8 w-8 rounded-full shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>
    );
  }

  const userEmail = user?.email || 'User';
  const userInitial = userEmail[0]?.toUpperCase() || 'U';
  const orgName = currentOrganization?.name || 'No Organization';
  const role = membership?.role || 'member';
  const isAdmin = role === 'owner' || role === 'admin';

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground transition-colors cursor-pointer"
        >
          <div className="h-8 w-8 shrink-0 rounded-full bg-muted flex items-center justify-center text-xs">
            {userInitial}
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight overflow-hidden">
            <span className="truncate font-medium">{userEmail}</span>
            <span className="truncate text-xs text-muted-foreground">{orgName}</span>
          </div>
          <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
        side="top"
        align="start"
        sideOffset={4}
      >
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium">{userEmail}</p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-muted-foreground">{orgName}</p>
              <Badge variant="secondary" className="text-xs capitalize">
                {role}
              </Badge>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate(`/${orgSlug}/settings`)}>
          <Settings className="mr-2 h-4 w-4" />
          Settings
        </DropdownMenuItem>
        {isAdmin && (
          <DropdownMenuItem onClick={() => navigate(`/${orgSlug}/settings/team`)}>
            <Users className="mr-2 h-4 w-4" />
            Team
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} variant="destructive">
          <LogOut className="mr-2 h-4 w-4" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
